import {
  applyRowAudienceToCatalog,
  applyRowAudienceToChats,
  applyScheduleSlackChannelsToCatalog,
  notebookSharingScopeForRow,
  type NotebookCatalog,
  type NotebookRow,
} from "../utils/notebookSeries"
import type { NotebookSlackCatalog } from "../utils/notebookSlack"
import type { NotebookSummary } from "../utils/notebookSnapshot"
import { DeleteNotebookDialog } from "./DeleteNotebookDialog"
import { NotebookListScheduleDialog } from "./NotebookListScheduleDialog"
import { NotebookSeriesDialog } from "./NotebookSeriesDialog"
import { NotebookShareDialog } from "./NotebookShareButton"
import { NotebookSlackDialog } from "./NotebookSlackDialog"

const notebookRowKey = (notebook: NotebookRow) =>
  `${notebook.kind}:${notebook.id}`

export function NotebookListDialogs({
  scope,
  organizationSlug,
  workspaceId,
  selectedSeries,
  schedulingRow,
  sharingRow,
  slackEditingRow,
  deletingRow,
  deletePending,
  slackCatalog,
  hrefFor,
  generatedHref,
  onCloseSeries,
  onOpenSchedule,
  onOpenShare,
  onOpenSlack,
  onCloseDelete,
  onConfirmDelete,
  onScheduleChanged,
  onUpdateChats,
  onUpdateCatalog,
  onUpdateSlackCatalog,
}: {
  scope: string
  organizationSlug: string
  workspaceId: string
  selectedSeries: NotebookRow | null
  schedulingRow: NotebookRow | null
  sharingRow: NotebookRow | null
  slackEditingRow: NotebookRow | null
  deletingRow: NotebookRow | null
  deletePending: boolean
  slackCatalog: NotebookSlackCatalog | null
  hrefFor: (id: string) => string
  generatedHref: (id: string) => string
  onCloseSeries: () => void
  onOpenSchedule: (row: NotebookRow | null) => void
  onOpenShare: (row: NotebookRow | null) => void
  onOpenSlack: (row: NotebookRow | null) => void
  onCloseDelete: () => void
  onConfirmDelete: () => void
  onScheduleChanged: () => void
  onUpdateChats: (
    updater: (prev: NotebookSummary[] | null) => NotebookSummary[] | null,
  ) => void
  onUpdateCatalog: (
    updater: (catalog: NotebookCatalog) => NotebookCatalog,
  ) => void
  onUpdateSlackCatalog: (catalog: NotebookSlackCatalog | null) => void
}) {
  return (
    <>
      <NotebookSeriesDialog
        row={selectedSeries}
        onClose={onCloseSeries}
        hrefFor={
          selectedSeries?.seriesStorage === "chat" ? hrefFor : generatedHref
        }
        onEditSchedule={(row) => {
          onCloseSeries()
          onOpenSchedule(row)
        }}
        onShare={(row) => {
          onCloseSeries()
          onOpenShare(row)
        }}
        onEditSlack={(row) => {
          onCloseSeries()
          onOpenSlack(row)
        }}
      />
      {schedulingRow && (
        <NotebookListScheduleDialog
          key={`schedule:${scope}:${notebookRowKey(schedulingRow)}`}
          open
          row={schedulingRow}
          organizationSlug={organizationSlug}
          workspaceId={workspaceId}
          onOpenChange={(openState) => {
            if (!openState) {
              onOpenSchedule(null)
            }
          }}
          onSaved={onScheduleChanged}
        />
      )}
      {slackEditingRow && (
        <NotebookSlackDialog
          key={`slack:${scope}:${notebookRowKey(slackEditingRow)}`}
          open
          row={slackEditingRow}
          organizationSlug={organizationSlug}
          workspaceId={workspaceId}
          initialCatalog={slackCatalog}
          onOpenChange={(openState) => {
            if (!openState) {
              onOpenSlack(null)
            }
          }}
          onSaved={({ scheduleId, slackChannelIds, catalog }) => {
            if (catalog !== undefined) {
              onUpdateSlackCatalog(catalog)
            }
            if (scheduleId && slackChannelIds !== undefined) {
              onUpdateCatalog((prev) =>
                applyScheduleSlackChannelsToCatalog(
                  prev,
                  scheduleId,
                  slackChannelIds,
                ),
              )
              // The save bumps the schedule's updated_at, which the response
              // omits; reload so the next edit sends the current version.
              onScheduleChanged()
            }
          }}
        />
      )}
      {sharingRow && (
        <NotebookShareDialog
          key={`${scope}:${notebookRowKey(sharingRow)}`}
          {...notebookSharingScopeForRow(
            sharingRow,
            organizationSlug,
            workspaceId,
          )}
          open
          onOpenChange={(openState) => {
            if (!openState) {
              onOpenShare(null)
            }
          }}
          onSaved={({ audience }) => {
            onUpdateChats((prev) =>
              applyRowAudienceToChats(prev, sharingRow, audience),
            )
            onUpdateCatalog((prev) =>
              applyRowAudienceToCatalog(prev, sharingRow, audience),
            )
          }}
        />
      )}
      <DeleteNotebookDialog
        open={deletingRow !== null}
        title={deletingRow?.title ?? ""}
        pending={deletePending}
        onClose={onCloseDelete}
        onConfirm={onConfirmDelete}
      />
    </>
  )
}
